<template>
    <div class="w-full md:w-auto min-w-[17.5rem]">
        <VueDatePicker
            input-class-name="form-input date-picker w-full"
            placeholder="Select a date range"
            :multi-calendars="true"
            :clearable="false"
            ref="picker"
            range
            auto-apply
            format="dd MMM, yyyy"
            :enable-time-picker="false"
            v-model="date"
        >
            <template #left-sidebar>
                <div class="sm:space-y-2">
                    <template v-for="(date, i) in ranges" :key="i">
                        <button
                            type="button"
                            class="dp__btn dp--preset-range py-2! px-2!"
                            :class="isActiveDate(date) ? 'bg-primary/80! text-white!' : ''"
                            @click.prevent="setDate(date)"
                            @keydown.enter.prevent="setDate(date)"
                            @keydown.space.prevent="setDate(date)"
                        >
                            {{ date.label }}
                        </button>
                    </template>
                </div>
            </template>
        </VueDatePicker>

        <template v-if="isFormPost">
            <input type="hidden" name="start_date" :value="currentFormatted.start_date">
            <input type="hidden" name="end_date" :value="currentFormatted.end_date">
        </template>
    </div>
</template>

<script>
import moment from "moment";
import Events from "../utils/Events";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'

export default {
    name: "DateRange",
    components: {
        VueDatePicker
    },
    emits: ['update:model-value'],
    props: {
        name: String,
        isFormPost: {
            type: Boolean,
            default: true,
        },
        modelBound: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: Object,
            default: null,
        },
        dateRanges: {
            type: Array,
            default: [],
        },
        updateUrl: {
            type: Boolean,
            default: true,
        }
    },
    data() {
        return {
            stopNextUpdate: false,

            date: [
                moment().subtract(30, 'days').hour(0).second(0).minute(0).toDate(),
                moment().hour(0).second(0).minute(0).toDate(),
            ],
        };
    },

    mounted() {
        if (this.isFormPost) {
            this.date = null;

            if (this.modelValue && this.modelValue.start_date && this.modelValue.end_date) {
                this.date = [
                    moment(this.modelValue.start_date).toDate(),
                    moment(this.modelValue.end_date).toDate(),
                ];
            }
        }

        this.$nextTick(() => {
            let searchParams = new URLSearchParams(window.location.search);

            if (searchParams.has('start_date') && searchParams.get('start_date')) {
                if (! this.date) {
                    this.date = [];
                }

                this.date[0] = moment(searchParams.get('start_date')).toDate();
            }

            if (searchParams.has('end_date') && searchParams.get('end_date')) {
                this.date[1] = moment(searchParams.get('end_date')).toDate();
            }

            if (this.isFormPost) {
                return;
            }

            this.update();
        });

        this.$watch('date', () => this.update());

        this.$watch('modelValue', function (e) {
            this.stopNextUpdate = true;

            this.date = [
                e.startDate.toDate(),
                e.endDate.toDate(),
            ];
        });
    },

    methods: {
        update() {
            if (this.stopNextUpdate) {
                this.stopNextUpdate = false;

                return;
            }

            Events.$emit('date-range-update', {
                name: this.name,
                startDate: this.date[0],
                endDate: this.date[1],
            });

            if (this.modelBound) {
                this.$emit('update:model-value', {
                    startDate: this.date[0] ? moment(this.date[0]) : null,
                    endDate: this.date[1] ? moment(this.date[1]) : null,
                });
            }

            if (! this.currentFormatted.start_date || ! this.currentFormatted.end_date) {
                return;
            }

            if (this.updateUrl) {
                let searchParams = new URLSearchParams(window.location.search);

                searchParams.set('start_date', this.currentFormatted.start_date);
                searchParams.set('end_date', this.currentFormatted.end_date);

                history.pushState(null, '', window.location.pathname + '?' + searchParams.toString());
            }
        },

        setDate(date) {
            this.$refs.picker.closeMenu();

            this.date = date.value;
        },

        isActiveDate(date) {
            if (! this.date) {
                return false;
            }

            let first = moment(date.value[0]).isSame(moment(this.date[0]), 'day');
            let second = moment(date.value[1]).isSame(moment(this.date[1]), 'day');

            return first && second;
        }
    },

    computed: {
        currentFormatted() {
              return {
                  start_date: this.date && this.date[0] ? moment(this.date[0]).format('YYYY-MM-DD') : null,
                  end_date: this.date && this.date[1] ? moment(this.date[1]).format('YYYY-MM-DD') : null,
              }
        },

        ranges() {
            if (this.dateRanges.length) {
                return this.dateRanges;
            }

            let yesterday = [
                { label: 'Yesterday', value: [moment().subtract(1, 'days').toDate(), moment().subtract(1, 'days').toDate()] },
            ];

            return [
                { label: 'Last 7 Days', value: [moment().subtract(7, 'days').toDate(), moment().toDate()] },
                { label: 'Last 30 Days', value: [moment().subtract(30, 'days').toDate(), moment().toDate()] },
                { label: 'Last 60 Days', value: [moment().subtract(60, 'days').toDate(), moment().toDate()] },
                { label: 'Last 90 Days', value: [moment().subtract(90, 'days').toDate(), moment().toDate()] },
                { label: 'This Year', value: [moment().startOf('year').toDate(), moment().endOf('year').toDate()] },
                { label: 'Last Year', value: [moment().subtract(1, 'year').startOf('year').toDate(), moment().subtract(1, 'year').endOf('year').toDate()] },
            ];
        },
    }
}
</script>

<style></style>
